import { useSyncExternalStore } from 'react';
import { highestContiguousSequence, isRetryableSendError, mergeMessages, shouldAutoRetry, type Message, type ReactionSummary, type SendMessageInput } from '@tradeoutlet/shared';
import { createUnsentMessages, type UnsentChatOp, type UnsentMessages } from '@tradeoutlet/data';
import { api } from './api';
import { hubDbName, indexedDbStore } from './idbStore';

export interface PendingMessage {
  client_message_id: string;
  body: string;
  parent_id: string | null;
  mentions: string[];
  attachment_ids: string[];
  attachments: Message['attachments'];
  call_id?: string | null;
  created_at: string;
  status: 'sending' | 'failed';
  error?: string;
  /** False when the server refused it (4xx): only the person can retry that. */
  retryable?: boolean;
  /** Automatic retries so far. */
  attempts?: number;
}

export interface ConversationState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  error?: string;
  /** Top-level messages, ascending by sequence. */
  messages: Message[];
  nextBefore: number | null;
  loadingOlder: boolean;
  /** Highest sequence such that every sequence <= it has been seen (gap-recovery baseline). */
  cursor: number;
  /** Thread replies by root id: inline previews (newest few, growing as older pages load) or the full sidebar thread. */
  threads: Record<string, Message[]>;
  /** Per root: sequence to pass as before= for older replies, or null when the loaded list is complete. */
  threadMore: Record<string, number | null>;
  /** Per root: distinct repliers, most recent first. */
  threadPeople: Record<string, string[]>;
  pending: PendingMessage[];
}

const EMPTY: ConversationState = { status: 'idle', messages: [], nextBefore: null, loadingOlder: false, cursor: 0, threads: {}, threadMore: {}, threadPeople: {}, pending: [] };

/**
 * Client-side message cache with ordering and gap recovery:
 *  - history pages are keyset-paginated by sequence (before=)
 *  - realtime/polled messages are merged by id and ordered by sequence
 *  - sequences above the contiguous cursor are tracked; any hole triggers an
 *    after_seq fetch until the cursor is contiguous again
 *  - sends are optimistic with a client_message_id so retries never duplicate
 */
export class MessageStore {
  private state = new Map<string, ConversationState>();
  private seen = new Map<string, Set<number>>();
  private ids = new Map<string, Set<string>>();
  private listeners = new Set<() => void>();
  private catchingUp = new Set<string>();
  private catchUpAgain = new Set<string>();
  version = 0;

  /** Signed-in user whose unsent messages are kept on the device (docs/offline.md: one store per user). */
  private userId: string | null = null;

  constructor(private readonly client: Pick<typeof api, 'messages' | 'sendMessage' | 'thread'> & Partial<Pick<typeof api, 'threadPreviews' | 'threadReplies'>> = api,
    private readonly unsent: UnsentMessages | null = null) {}

  /**
   * After sign-in / reload: show the user's unsent messages again (as "Not sent") and resend the ones that failed for
   * connection reasons, with their original client_message_id so nothing is duplicated.
   */
  async restoreUnsent(userId: string, opts: { resend?: boolean } = {}) {
    if (this.userId === userId) return;
    if (this.userId) this.reset(); // another account in this tab: never show the previous person's messages
    this.userId = userId;
    if (!this.unsent) return;
    const ops = await this.unsent.load(userId);
    if (this.userId !== userId) return; // signed out meanwhile
    for (const op of ops) {
      const cur = this.get(op.conversationId);
      if (cur.pending.some(x => x.client_message_id === op.id)) continue;
      const p: PendingMessage = { ...op.payload, attachments: op.payload.attachments as Message['attachments'], status: 'failed',
        error: op.error ?? 'Not sent', retryable: op.retryable, attempts: 0 };
      this.set(op.conversationId, { pending: [...cur.pending, p] });
    }
    if (ops.length && opts.resend !== false) await this.retryUnsent();
  }

  /** Sign-out: forget everything in memory and wipe this user's unsent messages from the device. */
  async forgetUser() {
    const u = this.userId;
    this.userId = null;
    this.reset();
    if (u && this.unsent) await this.unsent.clear(u);
  }

  private persist(id: string, p: PendingMessage) {
    if (!this.unsent || !this.userId) return;
    const op: UnsentChatOp = {
      id: p.client_message_id, kind: 'chat.send', userId: this.userId, conversationId: id, createdAt: p.created_at, attempts: p.attempts ?? 0,
      state: p.status === 'failed' ? 'failed' : 'queued', retryable: p.retryable !== false, ...(p.error ? { error: p.error } : {}),
      payload: { client_message_id: p.client_message_id, body: p.body, parent_id: p.parent_id, mentions: p.mentions, attachment_ids: p.attachment_ids,
        attachments: p.attachments ?? [], call_id: p.call_id ?? null, created_at: p.created_at },
    };
    void this.unsent.save(op);
  }
  private unpersist(clientMessageId: string) {
    if (this.unsent && this.userId) void this.unsent.remove(this.userId, clientMessageId);
  }

  subscribe = (fn: () => void) => { this.listeners.add(fn); return () => { this.listeners.delete(fn); }; };
  private emit() { this.version++; this.listeners.forEach(l => l()); }

  get(id: string): ConversationState { return this.state.get(id) ?? EMPTY; }
  private set(id: string, patch: Partial<ConversationState>) {
    this.state.set(id, { ...this.get(id), ...patch });
    this.emit();
  }

  reset() { this.state.clear(); this.seen.clear(); this.ids.clear(); this.emit(); }

  async load(id: string, force = false) {
    const cur = this.get(id);
    if (!force && (cur.status === 'loading' || cur.status === 'ready')) return;
    this.set(id, { status: 'loading', error: undefined });
    try {
      const page = await this.client.messages(id, { limit: 50 });
      this.seen.set(id, new Set());
      this.ids.set(id, new Set(page.messages.map(m => m.id)));
      this.state.set(id, { ...this.get(id), status: 'ready', messages: page.messages, nextBefore: page.next_before, cursor: page.latest_sequence });
      this.emit();
      void this.loadPreviews(id, page.messages);
    } catch (e) {
      this.set(id, { status: 'error', error: (e as Error).message });
    }
  }

  async loadOlder(id: string) {
    const cur = this.get(id);
    if (cur.loadingOlder || cur.nextBefore == null) return;
    this.set(id, { loadingOlder: true });
    try {
      const page = await this.client.messages(id, { before: cur.nextBefore, limit: 50 });
      const known = this.ids.get(id) ?? new Set();
      page.messages.forEach(m => known.add(m.id));
      this.ids.set(id, known);
      const now = this.get(id);
      this.set(id, { loadingOlder: false, messages: mergeMessages(now.messages, page.messages), nextBefore: page.next_before });
      void this.loadPreviews(id, page.messages);
    } catch {
      this.set(id, { loadingOlder: false });
    }
  }

  /**
   * t291u: load older pages until the top-level message with sequence `seq` (plus a little context above it) is in
   * the list, for search results and Jump to date. History stays contiguous from the latest message, so realtime,
   * gap recovery and the New marker keep working. Resolves true when that message is loaded.
   */
  async loadBackTo(id: string, seq: number, context = 5, maxPages = 80): Promise<boolean> {
    if (this.get(id).status !== 'ready') await this.load(id);
    for (let i = 0; i < maxPages; i++) {
      const cur = this.get(id);
      const oldest = cur.messages[0]?.sequence ?? Infinity;
      if (cur.nextBefore == null || oldest <= seq - context) break;
      if (cur.loadingOlder) { await new Promise(r => setTimeout(r, 50)); continue; }
      this.set(id, { loadingOlder: true });
      try {
        const page = await this.client.messages(id, { before: cur.nextBefore, limit: 100 });
        const known = this.ids.get(id) ?? new Set();
        page.messages.forEach(m => known.add(m.id));
        this.ids.set(id, known);
        this.set(id, { loadingOlder: false, messages: mergeMessages(this.get(id).messages, page.messages), nextBefore: page.next_before });
        void this.loadPreviews(id, page.messages);
      } catch {
        this.set(id, { loadingOlder: false });
        return false;
      }
    }
    return this.get(id).messages.some(m => m.sequence === seq);
  }

  /** Merge messages from realtime, polling, sends or gap recovery. */
  ingest(id: string, incoming: Message[]) {
    const cur = this.get(id);
    if (cur.status !== 'ready' || incoming.length === 0) return;
    const seen = this.seen.get(id) ?? new Set<number>();
    const known = this.ids.get(id) ?? new Set<string>();
    let messages = cur.messages;
    const threads = { ...cur.threads };
    const threadMore = { ...cur.threadMore };
    const threadPeople = { ...cur.threadPeople };
    const topLevel: Message[] = [];
    for (const m of incoming) {
      if (m.sequence > cur.cursor) seen.add(m.sequence);
      const isNew = !known.has(m.id);
      known.add(m.id);
      if (!m.parent_id) { topLevel.push(m); continue; }
      // Keep inline previews live: merge into a loaded thread, or start one for a visible root's first reply.
      if (threads[m.parent_id] || (isNew && messages.some(r => r.id === m.parent_id && r.reply_count === 0))) {
        threads[m.parent_id] = mergeMessages(threads[m.parent_id] ?? [], [m]);
        if (!(m.parent_id in threadMore)) threadMore[m.parent_id] = null;
      }
      if (isNew && m.kind === 'user' && !m.deleted_at) threadPeople[m.parent_id] = [m.sender_id, ...(threadPeople[m.parent_id] ?? []).filter(u => u !== m.sender_id)].slice(0, 5);
      if (isNew) {
        messages = messages.map(root => root.id === m.parent_id
          ? { ...root, reply_count: root.reply_count + 1, last_reply_at: m.created_at } : root);
      }
    }
    if (topLevel.length) messages = mergeMessages(messages, topLevel);
    const cursor = highestContiguousSequence(seen, cur.cursor);
    for (const s of [...seen]) if (s <= cursor) seen.delete(s);
    this.seen.set(id, seen);
    this.ids.set(id, known);
    // Drop optimistic copies that have now arrived.
    const arrived = new Set(incoming.map(m => m.client_message_id));
    const pending = cur.pending.filter(p => !arrived.has(p.client_message_id));
    for (const p of cur.pending) if (arrived.has(p.client_message_id)) this.unpersist(p.client_message_id);
    this.set(id, { messages, threads, threadMore, threadPeople, cursor, pending });
    if (seen.size > 0) void this.catchUp(id); // a hole remains -> fetch what we missed
  }

  /** after_seq recovery: fetch everything after the contiguous cursor (loops while has_more_after). */
  async catchUp(id: string) {
    if (this.get(id).status !== 'ready') return;
    if (this.catchingUp.has(id)) { this.catchUpAgain.add(id); return; }
    this.catchingUp.add(id);
    try {
      for (let i = 0; i < 20; i++) {
        const cur = this.get(id);
        const page = await this.client.messages(id, { after_seq: cur.cursor, limit: 200 });
        if (page.messages.length) this.ingest(id, page.messages);
        const after = this.get(id);
        // Sequences can be allocated without a visible row only if a send rolled back after
        // allocation - impossible here (counter row update and insert share a transaction),
        // so latest_sequence is a safe upper bound for the cursor.
        if (!page.has_more_after) {
          if (after.cursor < page.latest_sequence && page.messages.length === 0) this.set(id, { cursor: page.latest_sequence });
          break;
        }
      }
    } catch {
      /* transient - next event or poll retries */
    } finally {
      this.catchingUp.delete(id);
      if (this.catchUpAgain.delete(id)) void this.catchUp(id);
    }
  }

  applyUpdate(id: string, m: Partial<Message> & { id: string }) {
    const cur = this.get(id);
    if (cur.status !== 'ready') return;
    const patch = (list: Message[]) => list.map(x => (x.id === m.id ? { ...x, ...m } : x));
    const threads = Object.fromEntries(Object.entries(cur.threads).map(([k, v]) => [k, patch(v)]));
    this.set(id, { messages: patch(cur.messages), threads });
  }

  applyReactions(id: string, messageId: string, reactions: ReactionSummary[]) { this.applyUpdate(id, { id: messageId, reactions }); }

  async loadThread(conversationId: string, rootId: string) {
    const r = await this.client.thread(rootId);
    const cur = this.get(conversationId);
    const known = this.ids.get(conversationId) ?? new Set<string>();
    r.replies.forEach(m => known.add(m.id));
    this.ids.set(conversationId, known);
    this.set(conversationId, { threads: { ...cur.threads, [rootId]: r.replies }, threadMore: { ...cur.threadMore, [rootId]: null } });
    return r.root;
  }

  /** One batched request for the inline previews of every root in `messages` that has replies and isn't loaded yet. */
  async loadPreviews(conversationId: string, messages: Message[]) {
    if (!this.client.threadPreviews) return;
    const cur = this.get(conversationId);
    const ids = messages.filter(m => m.reply_count > 0 && !cur.threads[m.id]).map(m => m.id);
    for (let i = 0; i < ids.length; i += 100) {
      try {
        const r = await this.client.threadPreviews(conversationId, ids.slice(i, i + 100), 3);
        const now = this.get(conversationId);
        const known = this.ids.get(conversationId) ?? new Set<string>();
        const threads = { ...now.threads }, threadMore = { ...now.threadMore }, threadPeople = { ...now.threadPeople };
        for (const p of r.previews) {
          p.replies.forEach(m => known.add(m.id));
          threads[p.message_id] = mergeMessages(threads[p.message_id] ?? [], p.replies);
          if (!(p.message_id in threadMore) || threadMore[p.message_id] !== null) threadMore[p.message_id] = p.next_before;
          threadPeople[p.message_id] = p.participant_ids;
        }
        this.ids.set(conversationId, known);
        this.set(conversationId, { threads, threadMore, threadPeople });
      } catch { /* previews are best-effort; the reply count still links to the sidebar */ }
    }
  }

  /** Next page of older replies for an inline thread (keyset by per-conversation sequence). */
  async loadOlderReplies(conversationId: string, rootId: string, limit = 20) {
    const before = this.get(conversationId).threadMore[rootId];
    if (before == null || !this.client.threadReplies) return;
    const page = await this.client.threadReplies(rootId, { before, limit });
    const now = this.get(conversationId);
    const known = this.ids.get(conversationId) ?? new Set<string>();
    page.replies.forEach(m => known.add(m.id));
    this.ids.set(conversationId, known);
    this.set(conversationId, {
      threads: { ...now.threads, [rootId]: mergeMessages(now.threads[rootId] ?? [], page.replies) },
      threadMore: { ...now.threadMore, [rootId]: page.next_before },
    });
  }

  async send(id: string, input: Omit<SendMessageInput, 'client_message_id'> & { client_message_id?: string; attachments?: Message['attachments'] }) {
    const p: PendingMessage = {
      client_message_id: input.client_message_id ?? crypto.randomUUID(), body: input.body, parent_id: input.parent_id ?? null,
      mentions: input.mentions ?? [], attachment_ids: input.attachment_ids ?? [], attachments: input.attachments ?? [], call_id: input.call_id ?? null,
      created_at: new Date().toISOString(), status: 'sending',
    };
    const cur = this.get(id);
    this.set(id, { pending: [...cur.pending.filter(x => x.client_message_id !== p.client_message_id), p] });
    this.persist(id, p); // kept on the device until the server has it
    return this.deliver(id, p);
  }

  retry(id: string, clientMessageId: string) {
    const p = this.get(id).pending.find(x => x.client_message_id === clientMessageId);
    if (p) return this.deliver(id, { ...p, status: 'sending', error: undefined });
  }

  /** Connection back (online / realtime reconnected): resend what failed for connection reasons, same client_message_id. */
  retryUnsent() {
    const sent: Promise<unknown>[] = [];
    for (const [id, st] of this.state) {
      for (const p of st.pending) {
        if (shouldAutoRetry(p)) sent.push(this.deliver(id, { ...p, status: 'sending', error: undefined, attempts: (p.attempts ?? 0) + 1 }));
      }
    }
    return Promise.all(sent);
  }

  discard(id: string, clientMessageId: string) {
    this.set(id, { pending: this.get(id).pending.filter(x => x.client_message_id !== clientMessageId) });
    this.unpersist(clientMessageId);
  }

  private async deliver(id: string, p: PendingMessage) {
    this.set(id, { pending: this.get(id).pending.map(x => (x.client_message_id === p.client_message_id ? p : x)) });
    try {
      const m = await this.client.sendMessage(id, {
        client_message_id: p.client_message_id, body: p.body, parent_id: p.parent_id, mentions: p.mentions, attachment_ids: p.attachment_ids,
        ...(p.call_id ? { call_id: p.call_id } : {}),
      });
      this.ingest(id, [m]);
      // Not open (e.g. resent after a reload): ingest keeps no copy, so drop the pending row here.
      if (this.get(id).pending.some(x => x.client_message_id === p.client_message_id)) {
        this.set(id, { pending: this.get(id).pending.filter(x => x.client_message_id !== p.client_message_id) });
      }
      this.unpersist(p.client_message_id);
      return m;
    } catch (e) {
      const failed: PendingMessage = { ...p, status: 'failed', error: (e as Error).message, retryable: isRetryableSendError(e) };
      // t520u: refused (e.g. the channel's permissions just changed): refetch the channel so the composer catches up.
      if ((e as { status?: unknown } | null)?.status === 403) void import('./queryClient').then(q => q.queryClient.invalidateQueries({ queryKey: q.qk.conversation(id) })).catch(() => undefined);
      this.set(id, { pending: this.get(id).pending.map(x => (x.client_message_id === p.client_message_id ? { ...x, ...failed } : x)) });
      if (this.get(id).pending.some(x => x.client_message_id === p.client_message_id)) this.persist(id, failed);
      return null;
    }
  }
}

export const messageStore = new MessageStore(api, createUnsentMessages(userId => indexedDbStore<UnsentChatOp>(hubDbName(userId))));

export function useConversationState(id: string | undefined): ConversationState {
  return useSyncExternalStore(messageStore.subscribe, () => (id ? messageStore.get(id) : EMPTY));
}
